import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { PushNotificationFormSchema } from "../../../forms-schema/form.schema";
import Scrollbar from "../../../components/scrollbar/Scrollbar";

import {
  useGetTopicsQuery,
  usePushNotificationMutation,
} from "../../../redux/services/topics.service";

export default function PushNotification({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(PushNotificationFormSchema),
  });

  // Reset the Errors and FormFields when open or closed the Form drawer
  useEffect(() => {
    reset();
  }, [open, reset]);

  const {
    isLoading: isTopicsLoading,
    isFetching: isTopicsFetching,
    isError: isTopicsError,
    data: topics,
  } = useGetTopicsQuery();

  const [pushNotification, { isLoading }] = usePushNotificationMutation();

  const handleClick = async (values) => {
    console.log("push notification form values:", values);

    setLoading(true);
    try {
      const resultAction = await pushNotification({
        ...values,
      });

      if (resultAction?.data?.success) {
        reset();
        onClose();
      }
    } catch (error) {
      // Catch and handle any errors during the process
      console.error("Error!! Pushing Notification:", error);

      // Display error using notistack
      enqueueSnackbar("ERROR!! Pushing Notification.", {
        variant: "error", // Set the snackbar type to 'error'
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setLoading(false); // Ensure loading state is turned off in all cases
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: 360, border: "none", overflow: "hidden" },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            <div>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle1">Push Notification</Typography>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Box>
              <Divider />
            </div>

            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel
                  id="demo-simple-select-label"
                  error={Boolean(errors.topics)}
                >
                  <>
                    Topics <span style={{ color: "red" }}>*</span>{" "}
                  </>
                </InputLabel>
                <Select
                  {...register("topics", { required: true })}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label="Topics"
                  error={Boolean(errors.topics)}
                >
                  {isTopicsLoading || isTopicsFetching ? (
                    <MenuItem
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : (
                    topics?.data?.map((topic) => (
                      <MenuItem key={topic?.id} value={topic?.id}>
                        {topic?.name}
                      </MenuItem>
                    ))
                  )}
                </Select>

                <FormHelperText error={Boolean(errors.topics)}>
                  {errors.topics?.message}
                </FormHelperText>
              </FormControl>

              <TextField
                name="title"
                type="string"
                label={
                  <>
                    Title <span style={{ color: "red" }}>*</span>{" "}
                    {/* Adding red star */}
                  </>
                }
                {...register("title", { required: true })}
                error={Boolean(errors.title)}
                helperText={errors.title && errors.title.message} // Adjusted to properly show the error message
              />

              <TextField
                name="body"
                type="string"
                label={
                  <>
                    Body <span style={{ color: "red" }}>*</span>{" "}
                    {/* Adding red star */}
                  </>
                }
                {...register("body", { required: true })}
                error={Boolean(errors.body)}
                helperText={errors.body && errors.body.message} // Adjusted to properly show the error message
              />

              <LoadingButton
                loading={loading || isLoading}
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                onClick={handleSubmit(handleClick)}
              >
                Push Notification
              </LoadingButton>
            </Stack>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
